import * as React from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Badge, Banner, Icon, Select, Spinner, Table, Tbody, Td, Th, Thead, Tr } from "../components";
import { IMarkGridPathModel, IMarkGridSiblingTermModel, IMarkGridTeachingGroupModel, MarkExplanationModel } from "../api/APIClient";
import { useAsync } from "../general/useAsync";
import { useToast } from "../general/Toast";
import { getMarkExplanation } from "../activities/ActivitiesStore";

/**
 * Pieces every mark screen shares: the tile header that drills from Home to a subject to a division and steps between
 * the subject's terms, the class filter, and the explanation of a calculated mark.
 *
 * The class (teaching group) lives in the `?class=` query string rather than component state, so it survives the
 * drill-down and a link to a division opens on the same class.
 */

export function useClassFilter(): [string, (id: string) => void, string] {
    const [params, setParams] = useSearchParams();
    const value = params.get("class") ?? "";
    const set = (id: string) =>
        setParams(
            (current) => {
                const next = new URLSearchParams(current);
                if (id) next.set("class", id);
                else next.delete("class");
                return next;
            },
            { replace: true }
        );
    return [value, set, value ? `?class=${encodeURIComponent(value)}` : ""];
}

export const moduleRoute = (moduleID: string, query = "") => `/marks/${encodeURIComponent(moduleID)}${query}`;
export const groupRoute = (moduleID: string, groupID: string, query = "") =>
    `/marks/${encodeURIComponent(moduleID)}/g/${encodeURIComponent(groupID)}${query}`;
export const sheetRoute = (moduleID: string, groupID: string | undefined, query = "") =>
    groupID ? `${groupRoute(moduleID, groupID)}/sheet${query}` : `/marks/${encodeURIComponent(moduleID)}/sheet${query}`;

/** One step of the breadcrumb. The level being viewed is plain text; every level above it is one click away. */
const Crumb: React.FC<{ to: string; label: string; current?: boolean; children: React.ReactNode }> = ({ to, label, current, children }) =>
    current ? (
        <span aria-current="page" className="flex min-w-0 items-center font-semibold text-text-strong">
            {children}
        </span>
    ) : (
        <Link to={to} aria-label={label} className="flex min-w-0 items-center rounded px-0.5 text-primary-text hover:underline">
            {children}
        </Link>
    );

const Separator: React.FC = () => (
    <span aria-hidden className="shrink-0 text-[0.65rem] text-text-weak">
        <Icon icon={["fad", "chevron-right"]} />
    </span>
);

/**
 * The drill-down bar. The path back up - Home, the subject, each division - is a breadcrumb, so every level stays
 * one click away as it was on the legacy SCADSYS tiles, and the levels are data: a nested division adds a step rather
 * than needing a screen of its own. What the screen is *of* - the term and the class - sits on the right.
 */
export const TileBar: React.FC<{
    moduleID: string;
    moduleName: string;
    moduleDetail?: string;
    path: IMarkGridPathModel[];
    teachingGroups: IMarkGridTeachingGroupModel[];
    /** Every term of the subject; the switcher is hidden when there is only one. */
    terms?: IMarkGridSiblingTermModel[];
    current?: "module" | "group" | "sheet";
}> = ({ moduleID, moduleName, moduleDetail, path, teachingGroups, terms = [], current }) => {
    const navigate = useNavigate();
    const [classID, setClassID, query] = useClassFilter();

    return (
        <nav
            aria-label="Mark navigation"
            className="mb-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 rounded-card border border-border bg-surface px-3 py-2 shadow-card"
        >
            <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                <Crumb to="/marks" label="My subjects">
                    <Icon icon={["fad", "house"]} />
                </Crumb>
                <Separator />
                <Crumb to={moduleRoute(moduleID, query)} label={moduleName} current={current === "module"}>
                    <span className="truncate">{moduleName}</span>
                    {moduleDetail && <span className="ml-1.5 shrink-0 text-xs font-normal text-text-weak">{moduleDetail}</span>}
                </Crumb>
                {path.map((p, i) => (
                    <React.Fragment key={p.id}>
                        <Separator />
                        <Crumb to={groupRoute(moduleID, p.id ?? "", query)} label={p.name ?? ""} current={current === "group" && i === path.length - 1}>
                            <span className="truncate">{p.name}</span>
                        </Crumb>
                    </React.Fragment>
                ))}
                {current === "sheet" && (
                    <>
                        <Separator />
                        <span aria-current="page" className="font-semibold text-text-strong">
                            Mark sheet
                        </span>
                    </>
                )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
                <TermSwitcher terms={terms} classID={classID} view={current ?? "module"} />
                <Select
                    aria-label="Class or tutorial group"
                    value={classID}
                    placeholder="All students"
                    onChange={(e) => setClassID(e.target.value)}
                    className="!h-8 !w-auto min-w-[10rem] !py-0"
                >
                    {teachingGroups.map((g) => (
                        <option key={g.id} value={g.id}>
                            {g.name}
                        </option>
                    ))}
                </Select>
                <Link
                    to="/reports"
                    aria-label="Reports"
                    title="Print reports"
                    className="flex h-8 w-8 items-center justify-center rounded-btn text-text-weak hover:bg-surface-hover hover:text-text"
                >
                    <Icon icon={["fad", "print"]} />
                </Link>
                <button
                    type="button"
                    aria-label="Back"
                    title="Back"
                    onClick={() => navigate(-1)}
                    className="flex h-8 w-8 items-center justify-center rounded-btn text-text-weak hover:bg-surface-hover hover:text-text"
                >
                    <Icon icon={["fad", "chevron-left"]} />
                </button>
            </div>
        </nav>
    );
};

/**
 * Previous / next term, and a picker for any term. Each term is its own qualification module, so switching opens the
 * same view of the other term: the division with the same name when it has one (else the subject), and the class with
 * the same name when it has one (else every student, with a note saying so). The server works both matches out.
 */
const TermSwitcher: React.FC<{ terms: IMarkGridSiblingTermModel[]; classID: string; view: "module" | "group" | "sheet" }> = ({
    terms,
    classID,
    view,
}) => {
    const navigate = useNavigate();
    const toast = useToast();
    if (terms.length < 2) return null;

    const index = Math.max(0, terms.findIndex((t) => t.isViewing));
    const label = (t: IMarkGridSiblingTermModel) => `${t.cycleName ?? "Term"}${t.isCurrent ? " (current)" : ""}`;

    const go = (target?: IMarkGridSiblingTermModel) => {
        if (!target || target.isViewing) return;
        const moduleID = target.qualificationModuleID ?? "";
        const query = classID && target.teachingGroupID ? `?class=${encodeURIComponent(target.teachingGroupID)}` : "";
        if (classID && !target.teachingGroupID)
            toast.info(`${target.cycleName ?? "That term"} has no class with the same name, so every student is shown.`);
        const groupID = target.groupID ?? undefined;
        navigate(
            view === "sheet"
                ? sheetRoute(moduleID, groupID, query)
                : view === "group" && groupID
                  ? groupRoute(moduleID, groupID, query)
                  : moduleRoute(moduleID, query)
        );
    };

    const arrow =
        "flex w-8 items-center justify-center text-text-weak hover:bg-surface-hover hover:text-text " +
        "disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent";
    return (
        <div className="flex h-8 items-stretch overflow-hidden rounded-btn border-[1.5px] border-border-strong bg-surface" role="group" aria-label="Term">
            <button
                type="button"
                className={`${arrow} border-r border-border`}
                aria-label="Previous term"
                title={index > 0 ? label(terms[index - 1]) : "This is the first term"}
                disabled={index === 0}
                onClick={() => go(terms[index - 1])}
            >
                <Icon icon={["fad", "chevron-left"]} />
            </button>
            <Select
                aria-label="Term"
                value={terms[index]?.qualificationModuleID ?? ""}
                onChange={(e) => go(terms.find((t) => t.qualificationModuleID === e.target.value))}
                className="!h-full !w-auto min-w-[9rem] !rounded-none !border-0 !bg-transparent !py-0 font-medium"
            >
                {terms.map((t) => (
                    <option key={t.qualificationModuleID} value={t.qualificationModuleID}>
                        {label(t)}
                    </option>
                ))}
            </Select>
            <button
                type="button"
                className={`${arrow} border-l border-border`}
                aria-label="Next term"
                title={index < terms.length - 1 ? label(terms[index + 1]) : "This is the last term"}
                disabled={index >= terms.length - 1}
                onClick={() => go(terms[index + 1])}
            >
                <Icon icon={["fad", "chevron-right"]} />
            </button>
        </div>
    );
};

/**
 * Reads `MarkResult.Explain` - the provenance captured when the mark was calculated, including the names of the
 * inputs as they were then. It is never recomputed, so if the structure has changed since, this still shows what
 * actually produced the number.
 */
export const ExplanationPanel: React.FC<{ markResultID: string }> = ({ markResultID }) => {
    const explanation = useAsync<MarkExplanationModel>(() => getMarkExplanation(markResultID), [markResultID]);

    if (explanation.isLoading) {
        return (
            <div className="flex items-center gap-2 text-sm text-text-weak">
                <Spinner /> Loading explanation...
            </div>
        );
    }

    if (explanation.error) return <Banner tone="info">{explanation.error}</Banner>;

    const data = explanation.data;
    if (!data) return null;

    return (
        <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2 text-sm">
                <Badge tone="primary">{data.calculationMethod}</Badge>
                <Badge tone="neutral">{data.missingMarkPolicy}</Badge>
                <span className="text-text-weak">
                    {data.procedureName}
                    {data.versionNumber ? ` v${data.versionNumber}` : ""}
                    {data.calculatedAt ? ` - calculated ${new Date(data.calculatedAt).toLocaleString()}` : ""}
                </span>
            </div>

            <div className="overflow-hidden rounded-md border border-border bg-surface">
                <Table>
                    <Thead>
                        <Tr>
                            <Th>Input</Th>
                            <Th align="right">Result</Th>
                            <Th align="right">Out of</Th>
                            <Th align="right">Weight</Th>
                            <Th>Included</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {(data.inputs ?? []).map((input, i) => (
                            <Tr key={`${input.entityID}-${i}`}>
                                <Td>
                                    <span className="text-text-strong">{input.name || input.entityID}</span>
                                    <span className="ml-2 text-xs text-text-weak">{input.entityType}</span>
                                </Td>
                                <Td align="right">{input.result ?? <span className="text-text-weak">&mdash;</span>}</Td>
                                <Td align="right">{input.effectiveTotal ?? <span className="text-text-weak">&mdash;</span>}</Td>
                                <Td align="right">{input.weight}</Td>
                                <Td>
                                    <Badge tone={input.included ? "success" : "neutral"}>{input.included ? "Yes" : "No"}</Badge>
                                </Td>
                            </Tr>
                        ))}
                    </Tbody>
                </Table>
            </div>

            <p className="text-sm text-text">
                Produced <strong>{data.result}</strong> out of <strong>{data.total}</strong>.
            </p>

            {(data.warnings ?? []).length > 0 && (
                <div className="space-y-1">
                    {(data.warnings ?? []).map((w, i) => (
                        <Banner key={i} tone={w.severity === "Error" ? "danger" : "warning"}>
                            <strong>{w.code}</strong> {w.message}
                        </Banner>
                    ))}
                </div>
            )}
        </div>
    );
};
